<script setup lang="ts">
import type { IconKeys } from "../ui/Icon/Icon.interface"
import type { EventVault } from "@/models"
import { computed } from "vue"
import { FreemiumBadge } from "@/components"
import { Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

interface SummaryEventStatsCardProps {
  eventVault: EventVault
}

interface SummaryEventStatsCardStat {
  key: string
  icon: IconKeys
  label: string
  value?: number
  limit?: number
  showFreemiumBadge?: boolean
}

const props = defineProps<SummaryEventStatsCardProps>()

const { t } = useTranslation()

const isFreemiumEvent = computed(() => Boolean(props.eventVault?.freemiumMode))

const stats = computed<SummaryEventStatsCardStat[]>(() => {
  return [
    {
      key: "images",
      icon: "ImageUpload",
      label: t("summary_page.stats_card.stats.images_uploaded"),
      value: props.eventVault.totalPics,
      limit: props.eventVault.maxPhotoLimit,
    },
    {
      key: "videos",
      icon: "Video",
      label: t("summary_page.stats_card.stats.videos_uploaded"),
      value: props.eventVault.totalVids,
      showFreemiumBadge: isFreemiumEvent.value,
    },
    {
      key: "guests",
      icon: "User",
      label: t("summary_page.stats_card.stats.guests_attending"),
      value: props.eventVault.totalGuests,
    },
    {
      key: "guestbook",
      icon: "BookOpen",
      label: t("summary_page.stats_card.stats.guestbook_entries"),
      value: props.eventVault.totalMessages,
    },
  ]
})
</script>

<template>
  <div
    class="flex flex-col border border-secondary rounded-xl overflow-hidden"
    data-test="summary-event-stats-card"
  >
    <h2
      class="font-comfortaa text-lg font-bold bg-tertiary p-4"
      data-test="summary-event-stats-card-title"
    >
      {{ t("summary_page.stats_card.title") }}
    </h2>
    <div class="flex flex-col gap-2 md:gap-4 p-4">
      <p
        class="text-label"
        data-test="summary-event-stats-card-description"
      >
        {{ t("summary_page.stats_card.real_time_stats") }}
      </p>
      <div
        v-for="stat in stats"
        :key="stat.key"
        class="flex items-center gap-4 bg-tertiary p-4 rounded-md"
        :class="{ 'justify-between text-gray-400': stat.showFreemiumBadge }"
        data-test="summary-event-stats-row"
        :data-test-stat="stat.key"
      >
        <div class="flex items-center gap-4">
          <div class="bg-secondary rounded-md p-1">
            <Icon :icon="stat.icon" size="lg" />
          </div>
          <div class="flex flex-col">
            <div
              class="text-xl font-comfortaa -mb-1"
              data-test="summary-event-stats-value"
            >
              {{ stat.value }}
              <span
                v-if="stat.limit !== undefined && stat.limit !== null"
                class="text-gray-400 text-xs font-bold uppercase"
                data-test="summary-event-stats-limit"
              >
                {{ t("summary_page.stats_card.upload_limit", { limit: stat.limit }) }}
              </span>
            </div>
            <p class="text-label text-sm">
              {{ stat.label }}
            </p>
          </div>
        </div>
        <FreemiumBadge v-if="stat.showFreemiumBadge" variant="icon" />
      </div>
    </div>
  </div>
</template>
